// Re-exported for the app; its import-free home is what col-def modules read while this one is mid-cycle.
export { SupportedTagEntityTypes } from './tagEntityTypes';

import { BehaviorSubject } from 'rxjs';
import { SupportedTagEntityTypes } from './tagEntityTypes';
import { addError } from './Error';
import {
  deleteTag,
  deleteTagRelation,
  getTags as getTagsApi,
  postTag,
  postTagRelation,
  putTag
} from '../Api/Tags/tagApi';
import { Project } from './Project';
import { extractApiErrorMessage } from '../components/Compounds/McpAccess/extractApiErrorMessage';
import type { Quote, WorkflowOrder } from '../Api/Orders/ordersApi';
import { InventoryAsset, SupportCasesItem } from '../types';
import { accountScopeKey } from '../utils/accountIds';

export interface Tag {
  id: string;
  name: string;
  color: string;
  accountId: string;
  relations?: TagRelationSummary[];
  // Only the display name: listing tags needs no more than inventory view, and a whole portal user carries email, roles and permissions.
  updatedBy?: { name: string | null } | null;
}

// What the tags endpoints serve per relation: the kind alone, because the entity ids would name the very records a tag scope hides.
export interface TagRelationSummary {
  entityType: SupportedTagEntityTypes;
}

// The write shape — what a relation call addresses. It is not what the tag payload carries back.
export interface TagRelation {
  id: string;
  entityId: string;
  entityType: SupportedTagEntityTypes;
  tagId: string;
}

export type SupportedTagEntities = Project | InventoryAsset | SupportCasesItem | WorkflowOrder | Quote;

export const primaryColors: string[] = [
  '#D96E2B',
  '#3B6FE8',
  '#9EA688',
  '#B8D936',
  '#8777D9',
  '#DABD2C',
  '#3F527F',
  '#FCCE7E',
  '#C3D1F2',
  '#4B39A2'
];
export const secondaryColors: string[] = [
  '#7D4726',
  '#D9992B',
  '#6B5F20',
  '#525F1D',
  '#202A40',
  '#281D5F',
  '#4D2C17',
  '#7A8BDE',
  '#64664C',
  '#765823',
  '#3C2F18',
  '#383216',
  '#353E13',
  '#424D85',
  '#2F365D',
  '#343525',
  '#A4A4A4',
  '#B1B5A7',
  '#5D5D5D',
  '#A0A7BA',
  '#292929',
  '#41423D',
  '#6E7485',
  '#75776F',
  '#333741'
];
const forbiddenColors: string[] = [
  '#f5f5f5', // Light Gray
  '#FFFFFF' // White
];

const generateRandomColor = (): string => {
  const hexChars = '0123456789ABCDEF';
  let colorString = '#';
  for (let i = 0; i < 6; i++) {
    colorString += hexChars[Math.floor(Math.random() * 16)];
  }
  return colorString;
};

// On demand: a static import would put this module on the col-def ↔ store/User cycle.
const selectedAccountIds = async (): Promise<string[]> => (await import('./User')).getSelectedAccountIdsForUrl();

// Keyed by scope, not a single slot: coalescing across accounts would serve a scoped caller another account's tags.
const tagFetchInFlight = new Map<string, Promise<void>>();

const defaultTagData: Tag[] = [];
const tagSubject = new BehaviorSubject<Tag[]>(defaultTagData);
const tagsLoadingSubject = new BehaviorSubject<boolean>(false);
const lastFetchedAccountKeySubject = new BehaviorSubject<string | null>(null);
const emitErrorState = (message?: string): void => addError('tag', message);

export const getTagSubject = (): BehaviorSubject<Tag[]> => tagSubject;
export const getTagLoadingSubject = (): BehaviorSubject<boolean> => tagsLoadingSubject;
export const getLastFetchedAccountKeySubject = (): BehaviorSubject<string | null> => lastFetchedAccountKeySubject;
export const getTags = (): Tag[] => tagSubject.value;
export const getTagById = (id: string): Tag | undefined => tagSubject.value.find((tag) => tag.id === id);

export const generateTagColor = (): string => {
  const existingTagColors = getTags().map((tag) => tag.color);
  const primaryColorOption = primaryColors.find((color) => !existingTagColors.includes(color));
  if (primaryColorOption) return primaryColorOption;
  const secondaryColorOption = secondaryColors.find((color) => !existingTagColors.includes(color));
  if (secondaryColorOption) return secondaryColorOption;

  const MAX_ATTEMPTS = 1000;
  let tertiaryColorOption;
  let attempts = 0;
  while (!tertiaryColorOption && attempts < MAX_ATTEMPTS) {
    const randomColor = generateRandomColor();
    if (!existingTagColors.includes(randomColor) && !forbiddenColors.includes(randomColor)) {
      tertiaryColorOption = randomColor;
    }
    attempts++;
  }
  return tertiaryColorOption || forbiddenColors[0]; // Fallback to first forbidden color if all attempts fail, user will be able to edit color at some point
};

export const getAvailablePresetColors = (): string[] => {
  const existingTagColors = getTags().map((tag) => tag.color);
  return [...primaryColors, ...secondaryColors].filter((color) => !existingTagColors.includes(color));
};

export const tagIsUnique = (tag: Tag): boolean => {
  const existingTags = getTags();
  return !existingTags.some(
    (t) =>
      (t.name.toLowerCase() === tag.name.toLowerCase() || tag.color.toLowerCase() === t.color.toLowerCase()) &&
      t.id !== tag.id
  );
};

const readTags = async (forceRefresh: boolean, accountIds: string[], accountKey: string): Promise<void> => {
  // Deliberately not gated on tagsLoadingSubject: that flag is global, so it made a read for one account
  // skip while another account's read was in flight, and the caller then read the wrong scope's tags.
  if (!forceRefresh && lastFetchedAccountKeySubject.value === accountKey && tagSubject.getValue().length > 0) {
    return;
  }

  tagsLoadingSubject.next(true);
  try {
    const { data, error } = await getTagsApi(accountIds);
    if (data) {
      tagSubject.next(data);
      lastFetchedAccountKeySubject.next(accountKey);
      emitErrorState();
    }

    if (error) {
      emitErrorState(error.message);
      tagSubject.next(defaultTagData);
      lastFetchedAccountKeySubject.next(null);
    }
  } catch (e) {
    // A throw here would otherwise wedge the loading flag on, and every reader of the store waits on it.
    emitErrorState(e instanceof Error ? e.message : String(e));
    tagSubject.next(defaultTagData);
    lastFetchedAccountKeySubject.next(null);
  } finally {
    tagsLoadingSubject.next(false);
  }
};

export const fetchTags = async (forceRefresh: boolean = false, accountId?: string): Promise<void> => {
  const accountIds = accountId ? [accountId] : await selectedAccountIds();
  const accountKey = accountScopeKey(accountIds);

  const inFlight = tagFetchInFlight.get(accountKey);
  // A forced caller queues behind the shared read, not joins it: handleTagChange reads the subject right after.
  if (inFlight) {
    await inFlight;
    if (!forceRefresh) return;
  }

  const request = readTags(forceRefresh, accountIds, accountKey);
  tagFetchInFlight.set(accountKey, request);
  try {
    await request;
  } finally {
    if (tagFetchInFlight.get(accountKey) === request) tagFetchInFlight.delete(accountKey);
  }
};

export const addTag = async (
  tag: Omit<Tag, 'id' | 'accountId'>,
  refetchTags: boolean = false,
  accountId?: string
): Promise<Tag | null> => {
  const { data, error } = await postTag(tag, accountId);
  if (data) {
    if (refetchTags) fetchTags(true, accountId);
    emitErrorState();
  }

  if (error) {
    emitErrorState(error.message);
  }

  return data;
};

export const updateTag = async (tag: Tag, refetchTags: boolean = false, accountId?: string): Promise<Tag | null> => {
  if (!tagIsUnique(tag)) {
    emitErrorState('Tag with same name or color already exists');
    return null;
  }
  const { data, error } = await putTag(tag);

  if (data) {
    if (refetchTags) fetchTags(true, accountId);
    emitErrorState();
  }

  if (error) {
    emitErrorState(error.message);
  }

  return data;
};

export const removeTag = async (id: string, refetchTags: boolean = false, accountId?: string): Promise<boolean> => {
  const { error } = await deleteTag(id);

  if (!error) {
    if (refetchTags) fetchTags(true, accountId);
    emitErrorState();
  } else {
    emitErrorState(error.message);
    return false;
  }

  return true;
};

export interface TagChangeResult {
  tags: Tag[];
  error?: string;
}

export const handleTagChange = async (
  tags: (Tag | string)[],
  entityId: string,
  entityType: SupportedTagEntityTypes,
  prevEntityTags: Tag[],
  entityAccountId?: string
): Promise<TagChangeResult> => {
  const newTags: Omit<Tag, 'id' | 'accountId'>[] = tags.reduce((acc: Omit<Tag, 'id' | 'accountId'>[], tag) => {
    if (typeof tag === 'string') {
      acc.push({
        name: tag,
        color: generateTagColor()
      });
    }
    return acc;
  }, []);

  // The tagged record's account, not the first one selected: the api binds a tag to its account and refuses any other.
  const accountId = entityAccountId ?? (await selectedAccountIds())[0];

  const creations = await Promise.all(
    newTags.map(async (tag) => ({ name: tag.name, created: await addTag(tag, false, accountId) }))
  );
  const createdTags = creations.map(({ created }) => created).filter((tag): tag is Tag => !!tag);
  // A tag that never got created queues no relation, so without this the chip just reverts and nothing is reported.
  const uncreated = creations.filter(({ created }) => !created).map(({ name }) => name);
  await fetchTags(true);

  const currentTags = getTags();

  const relationsToAdd: Omit<TagRelation, 'id'>[] = [];
  const relationsToRemove: Omit<TagRelation, 'id'>[] = [];
  tags.forEach((tag) => {
    if (typeof tag === 'string') {
      const existingTag = currentTags.find((t) => t.name === tag);
      if (existingTag) {
        relationsToAdd.push({
          entityId,
          entityType,
          tagId: existingTag.id
        });
      }
    } else if (!prevEntityTags.some((prevTag) => prevTag.id === tag.id)) {
      relationsToAdd.push({
        entityId,
        entityType,
        tagId: tag.id
      });
    }
  });

  prevEntityTags.forEach((prevTag) => {
    if (!tags.some((tag) => typeof tag === 'object' && tag.id === prevTag.id)) {
      relationsToRemove.push({
        entityId,
        entityType,
        tagId: prevTag.id
      });
    }
  });

  const addPromises = relationsToAdd.map(async (relation) => ({
    tagId: relation.tagId,
    error: (await postTagRelation(relation.tagId, relation.entityId, relation.entityType))?.error
  }));

  const removePromises = relationsToRemove.map(async (relation) => ({
    tagId: relation.tagId,
    error: (await deleteTagRelation(relation.tagId, relation.entityId, relation.entityType))?.error
  }));

  const [addResults, removeResults] = await Promise.all([Promise.all(addPromises), Promise.all(removePromises)]);
  const failedAdds = addResults.filter(({ error }) => error);
  const failure = failedAdds[0] || removeResults.find(({ error }) => error);

  const creationError = uncreated.length
    ? `Could not create ${uncreated.map((name) => `"${name}"`).join(', ')}.`
    : undefined;

  let reportedError: string | undefined;
  if (failure || creationError) {
    // Only tags stranded by their own failed add; a concurrent edit that reused one loses it (accepted).
    const strandedTags = failure ? createdTags.filter((tag) => failedAdds.some((add) => add.tagId === tag.id)) : [];
    let rollbackError: string | undefined;
    if (strandedTags.length) {
      const rollbacks = await Promise.all(strandedTags.map((tag) => deleteTag(tag.id)));
      await fetchTags(true);
      const refused = rollbacks.find((result) => result?.error)?.error;
      rollbackError = refused ? extractApiErrorMessage(refused, '') : undefined;
    }
    // The api's own words, not axios's status line: the account-binding refusal is only legible from the body.
    reportedError =
      [creationError, extractApiErrorMessage(failure?.error, ''), rollbackError].filter(Boolean).join('; ') ||
      undefined;
    emitErrorState(reportedError);
  }

  const resolvedTags = tags.map((tag) => {
    if (typeof tag === 'string') {
      return currentTags.find((t) => t.name === tag) || { id: '', name: tag, color: generateTagColor(), accountId: '' };
    }
    return tag;
  });

  // Returned as well as reported: nothing renders the tag error domain, so the editor raises the snackbar.
  return { tags: resolvedTags, error: reportedError };
};
